import { Injectable } from '@angular/core';
import { ENVIRONMENT } from '../../../../environment/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
    CourseListingDetailInterface,
    CourseListingDetailModel,
    FeaturedBanner,
    ProgramChapterDetailsInterface,
    ProgramChapterListingInterface,
    ProgramDetails,
    ProgramsFilterModel,
    RecommendedProgramsResponse,
} from '../../models/webinar-course.model';
import { map, Observable } from 'rxjs';
import { ApiResponse, Pagination } from '../../models/api-response.model';
import { ProgramFilterInterface } from '../../models/filter.model';
import { UpComingClassesFilterInterface } from '../../../features/home/models/home.model';
import { ProgramType } from '../../enum/webinar-course.enum';

@Injectable({
    providedIn: 'root',
})
export class ProgramService {
    apiRoute = `${ENVIRONMENT.apiDomain}/api/v1/Program`;

    constructor(private httpClient: HttpClient) {}

    public getProgramDetails(id: string): Observable<ProgramDetails> {
        return this.httpClient.get<ApiResponse<ProgramDetails>>(`${this.apiRoute}/${id}`).pipe(
            map((response) => {
                return (response.result || {}) as ProgramDetails;
            }),
        );
    }

    public getProgramChapterDetails(id: string): Observable<ProgramChapterListingInterface[]> {
        return this.httpClient
            .get<ApiResponse<ProgramChapterListingInterface[]>>(`${this.apiRoute}/${id}/program-chapters`)
            .pipe(
                map((response) => {
                    return (response.result || {}) as ProgramChapterListingInterface[];
                }),
            );
    }

    public getProgramChapterIndividualDetails(id: string, token: string): Observable<ProgramChapterDetailsInterface> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${token}`,
        });

        return this.httpClient
            .get<
                ApiResponse<ProgramChapterDetailsInterface>
            >(`${this.apiRoute}/program-chapters-detail/${id}`, { headers })
            .pipe(
                map((response) => {
                    return (response.result || {}) as ProgramChapterDetailsInterface;
                }),
            );
    }

    public enrolProgram(id: string): Observable<ApiResponse<boolean>> {
        return this.httpClient.post<ApiResponse<boolean>>(`${this.apiRoute}/${id}/enrol`, null).pipe(
            map((response) => {
                return (response.result || {}) as ApiResponse<boolean>;
            }),
        );
    }

    public getUpcomingClasses(
        body: UpComingClassesFilterInterface,
    ): Observable<Pagination<CourseListingDetailModel[]>> {
        const programListFiltersApi = `${this.apiRoute}/update-classes`;

        return this.httpClient
            .post<ApiResponse<Pagination<CourseListingDetailInterface[]>>>(programListFiltersApi, body)
            .pipe(
                map((response) => {
                    const programs = response.result.data.map(
                        (program: CourseListingDetailInterface) => new CourseListingDetailModel(program),
                    );

                    return { ...response.result, data: programs };
                }),
            );
    }

    public getProgramListByFilters(
        requestBody: ProgramsFilterModel,
        isFavourite = false,
    ): Observable<Pagination<CourseListingDetailModel[]>> {
        const programListFiltersApi = isFavourite ? `${this.apiRoute}/favorites` : `${this.apiRoute}/filter`;

        return this.httpClient
            .post<ApiResponse<Pagination<CourseListingDetailInterface[]>>>(programListFiltersApi, requestBody)
            .pipe(
                map((response) => {
                    const programs = response.result.data.map(
                        (program: CourseListingDetailInterface) => new CourseListingDetailModel(program),
                    );

                    return { ...response.result, data: programs };
                }),
            );
    }

    public getProgramListByFilters2(
        requestBody: ProgramsFilterModel,
        isFavourite = false,
    ): Observable<Pagination<CourseListingDetailModel[]>> {
        const programListFiltersApi = isFavourite ? `${this.apiRoute}/favorites` : `${this.apiRoute}/filter2`;

        return this.httpClient
            .post<ApiResponse<Pagination<CourseListingDetailInterface[]>>>(programListFiltersApi, requestBody)
            .pipe(
                map((response) => {
                    const programs = response.result.data.map(
                        (program: CourseListingDetailInterface) => new CourseListingDetailModel(program),
                    );

                    return { ...response.result, data: programs };
                }),
            );
    }

    public getProgramListByLatestCourse(
        requestBody: ProgramsFilterModel,
    ): Observable<Pagination<CourseListingDetailModel[]>> {
        return this.httpClient
            .post<
                ApiResponse<Pagination<CourseListingDetailInterface[]>>
            >(`${this.apiRoute}/filter/latest-courses`, requestBody)
            .pipe(
                map((response) => {
                    const programs = response.result.data.map(
                        (program: CourseListingDetailInterface) => new CourseListingDetailModel(program),
                    );
                    
                    // Debug Log (Front-End)
                    // console.log("[On Fetch Latest Course Programs] Response: ", response);
                    // console.log("[On Fetch Latest Course Programs] Parsed Programs Result: ", programs);

                    return { ...response.result, data: programs };
                }),
            );
    }

    public bookmarkProgram(id: string, isFavorite: boolean): Observable<boolean> {
        const programFavoriteApi = `${this.apiRoute}/${id}/favorite`;
        const params = {
            isFavorite: isFavorite,
        };

        return this.httpClient.post<ApiResponse<boolean>>(programFavoriteApi, null, { params: params }).pipe(
            map((response) => {
                return (response.result || false) as boolean;
            }),
        );
    }

    public getTopicFilterListing(): Observable<ProgramFilterInterface> {
        return this.httpClient.get<ApiResponse<ProgramFilterInterface>>(`${this.apiRoute}/filters`).pipe(
            map((response) => {
                return response.result as ProgramFilterInterface;
            }),
        );
    }

    public getCourseFeaturedBannerInfo(): Observable<ApiResponse<FeaturedBanner>> {
        return this.httpClient.get<ApiResponse<FeaturedBanner>>(`${this.apiRoute}/featured-webinar`);
    }

    public updateProgramVideoViewCount(programId: string, programType: ProgramType): Observable<ApiResponse<boolean>> {
        return this.httpClient
            .post<
                ApiResponse<boolean>
            >(`${this.apiRoute}/${programId}/video-view-count?programType=${programType}`, null)
            .pipe(
                map((response) => {
                    return (response.result || {}) as ApiResponse<boolean>;
                }),
            );
    }

    public updateProgramViewCount(id: string): Observable<ApiResponse<boolean>> {
        return this.httpClient.post<ApiResponse<boolean>>(`${this.apiRoute}/${id}/view-count`, null);
    }

    public getProgramInProgressStatus(id: string): Observable<ApiResponse<boolean>> {
        return this.httpClient.get<ApiResponse<boolean>>(`${this.apiRoute}/${id}/is-in-progress`);
    }

    public getRecommendedPrograms(): Observable<ApiResponse<RecommendedProgramsResponse>> {
        return this.httpClient.get<ApiResponse<RecommendedProgramsResponse>>(`${this.apiRoute}/recommended`);
    }
}
